import { css } from '@emotion/react';
import type { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { BsCalendar3, BsClock } from 'react-icons/bs';
import { FiRepeat } from 'react-icons/fi';
import type { Weekday } from 'rrule';
import { Popover, PopoverPanel } from '@/components/Popover';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import {
  type RecurrenceOption,
  RecurrenceSelect,
} from '@/pages/Schedule/components';
import clockIcon from '@/pages/Schedule/icons/clock.png';
import { getDateSummary, type ParsedRRule } from '@/pages/Schedule/utils';
import { Alert, AlertTitle, useDisclosure, VStack } from '@/ui';
import { EntryModalField, EntryModalFieldButton } from '../';
import { DateRangeInput } from './components/DateRangeInput';
import { TimeInput } from './components/TimeInput';

type Props = ParsedRRule & {
  isOnDay: boolean;
  startDate: DateTime;
  endDate: DateTime;
  hasAnyMessages: boolean;
  rsvpEnabled: boolean;
  rule?: string | null;
  onStartDateChange: (date: DateTime) => void;
  onEndDateChange: (date: DateTime) => void;
  onUntilDateChange: (date: DateTime | null) => void;
  onAddTimeClick: () => void;
  onRemoveTimeClick: () => void;
  onStartTimeChange: (startTime: DateTime) => void;
  onEndTimeChange: (endTime: DateTime) => void;
  onRecurrenceChange: (option: RecurrenceOption) => void;
  onWeekdayChange: (weekday: Weekday[]) => void;
  onMonthChange: (weekday: Weekday | null) => void;
};

export const WhenSheet = ({
  rule,
  frequency,
  byWeekday,
  interval,
  hasAnyMessages,
  rsvpEnabled,
  isOnDay,
  startDate,
  endDate,
  untilDate,
  onRecurrenceChange,
  onWeekdayChange,
  onMonthChange,
  onStartDateChange,
  onEndDateChange,
  onUntilDateChange,
  onStartTimeChange,
  onEndTimeChange,
  onAddTimeClick,
  onRemoveTimeClick,
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation('entryModal');
  const isMobile = useIsMobileBreakpoint();

  const trigger = (
    <EntryModalField
      icon={<img alt="" height="18px" src={clockIcon} width="18px" />}
    >
      <EntryModalFieldButton
        alignItems="center"
        data-testid="when-sheet-button"
        display="block"
        size="sm"
        textAlign="left"
        whiteSpace="break-spaces"
        css={css`
          &:first-letter {
            text-transform: capitalize;
          }
        `}
        onClick={onOpen}
      >
        {getDateSummary(startDate, endDate, isOnDay, rule)}
      </EntryModalFieldButton>
    </EntryModalField>
  );

  return (
    <Popover
      data-testid="when-sheet"
      isOpen={isOpen}
      trigger={trigger}
      contentProps={{
        minW: '400px',
        width: 'auto',
        'data-testid': 'when-sheet',
      }}
      onClose={onClose}
    >
      <PopoverPanel disableFocusLock title={isMobile && t('when.sheet_title')}>
        <VStack alignItems="start" p="5" spacing="2">
          {!!rule && hasAnyMessages && (
            <Alert alignItems="flex-start" p="2" status="warning">
              <AlertTitle
                fontSize="sm"
                fontWeight="normal"
                lineHeight="normal"
                maxW={{ md: '300px' }}
              >
                {t('when.message_alert')}
              </AlertTitle>
            </Alert>
          )}
          <EntryModalField icon={<FiRepeat />} iconPlacement="top">
            <RecurrenceSelect
              byWeekday={byWeekday}
              frequency={frequency}
              interval={interval}
              rule={rule ?? null}
              startDate={startDate}
              variant="notion"
              onChange={onRecurrenceChange}
              onMonthChange={onMonthChange}
              onWeekdayChange={onWeekdayChange}
            >
              {rsvpEnabled && (
                <Alert fontSize="sm" px="2" py="1" status="warning">
                  {t('when.rsvp_alert')}
                </Alert>
              )}
            </RecurrenceSelect>
          </EntryModalField>
          <EntryModalField
            containerProps={{ gap: 2, flex: 0 }}
            icon={<BsCalendar3 />}
          >
            <DateRangeInput
              endDate={endDate}
              hasRecurrence={frequency !== null}
              isOnDay={isOnDay}
              startDate={startDate}
              untilDate={untilDate}
              onEndChange={onEndDateChange}
              onStartChange={onStartDateChange}
              onUntilChange={onUntilDateChange}
            />
          </EntryModalField>

          <EntryModalField
            containerProps={{ gap: 2, flex: 0 }}
            icon={<BsClock />}
          >
            <TimeInput
              endDate={endDate}
              hasRecurrence={frequency !== null}
              isOnDay={isOnDay}
              startDate={startDate}
              onAddTimeClick={onAddTimeClick}
              onClose={onClose}
              onEndTimeChange={onEndTimeChange}
              onRemoveTimeClick={onRemoveTimeClick}
              onStartTimeChange={onStartTimeChange}
            />
          </EntryModalField>
        </VStack>
      </PopoverPanel>
    </Popover>
  );
};
